import React, { useState, useEffect } from 'react';
import './firebase/config'; // Initialize Firebase
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Import styles
import './styles/App.css';
import './components/layout/MobileNavigation.css';
import './styles/DarkMode.css'; // Global dark mode styles
import './styles/InputFieldEnhancements.css'; // Global input field enhancements
import 'react-toastify/dist/ReactToastify.css';

// Import AppRoutes
import AppRoutes from './AppRoutes';
import SplashScreen from './components/ui/SplashScreen';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NavigationProvider } from './contexts/NavigationContext';
import { CartProvider } from './contexts/CartContext';
import { MobileMenuProvider } from './contexts/MobileMenuContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { MessageProvider } from './contexts/MessageContext';
import { ReviewProvider } from './contexts/ReviewContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { ChatProvider } from './contexts/ChatContext';
import ChatButton from './components/chatbot/user/ChatButton';
import Notification from './components/ui/Notification';
import { ToastContainer } from 'react-toastify';
import ChatWindow from './components/chatbot/user/ChatWindow';
import MobileNavigationSeller from './components/layout/MobileNavigation';
import MobileNavigationBuyer from './components/layout/MobileNavigationBuyer';
import useEnhancedDeviceDetection from './hooks/useEnhancedDeviceDetection';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPasswordOTP from './pages/ResetPasswordOTP';
import NewPassword from './pages/NewPassword';
import PasswordResetSuccess from './pages/PasswordResetSuccess';
import RegisterStep1 from './pages/RegisterStep1';
import RegisterStep2 from './pages/RegisterStep2';
import RegisterStep3 from './pages/RegisterStep3';
import VerifyEmail from './pages/VerifyEmail';
import Profile from './pages/Profile';
import Products from './pages/Products';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Wishlist from './pages/Wishlist';
import Orders from './pages/Orders';
import OrderTracking from './pages/OrderTracking';
import Checkout from './pages/Checkout';
import OrderConfirmation from './pages/OrderConfirmation';
import Settings from './pages/Settings';
import Messages from './pages/Messages';
import AlertsPage from './pages/alerts/AlertsPage';
import SellerProducts from './pages/seller/Products';
import SellerDashboard from './pages/seller/Dashboard';
import SellerProductDetail from './pages/seller/SellerProductDetail';
import AddProduct from './pages/seller/AddProduct';
import EditProduct from './pages/seller/EditProduct';
import AdminDashboard from './pages/admin/Dashboard';
import AdminUsers from './pages/admin/Users';
import AdminProducts from './pages/admin/Products';
import AdminOrders from './pages/admin/Orders';
import AdminLayout from './pages/admin/AdminLayout';
import ProfessionalSellers from './pages/admin/ProfessionalSellers';
import SellerVerification from './pages/admin/SellerVerification';
import AdminManagement from './pages/admin/AdminManagement';
import AdminChat from './pages/AdminChat';
import AdminLogin from './pages/AdminLogin';
import NotFound from './pages/NotFound';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import Disclaimer from './pages/Disclaimer';
import AboutUs from './pages/AboutUs';
import FAQ from './pages/FAQ';
import ContactUs from './pages/ContactUs';

// Protected route component
const ProtectedRoute = ({ children, requiredRole }) => {
  const { currentUser, userRole, loading } = useAuth();
  const location = useLocation();

  const disableAdminGuard =
    requiredRole === 'admin' &&
    String(process.env.REACT_APP_DISABLE_ADMIN_GUARD).toLowerCase() === 'true';

  // Show loader while authentication is being checked
  if (loading) {
    return null; // Let AuthContext handle the loading state
  }

  if (disableAdminGuard) {
    return children;
  }

  if (!currentUser) {
    const loginPath = requiredRole === 'admin' ? '/admin/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  if (requiredRole === 'admin') {
    const isAdminOrStaff =
      userRole === 'admin' ||
      currentUser?.role === 'admin' ||
      currentUser?.is_staff ||
      currentUser?.is_superuser;

    if (!isAdminOrStaff) {
      return <Navigate to="/" replace />;
    }

    return children;
  }

  if (requiredRole && userRole && userRole !== requiredRole) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Component to conditionally render ChatWindow for all users except admins
const UserChatComponent = () => {
  const { userRole } = useAuth();
  
  // Don't show chat for admin users
  if (userRole === 'admin') {
    return null;
  }
  
  return <ChatWindow />;
};

// Main App Wrapper with Loading and Splash Screen
// Component to handle mobile navigation with auth context
const MobileNavigationWrapper = () => {
  const { isMobile } = useEnhancedDeviceDetection();
  const { userRole } = useAuth();
  
  // Render with CSS-based visibility control to avoid blocking initial render
  // The CSS will hide this on desktop screens
  return (
    <div className={isMobile ? 'd-block' : 'd-none d-md-none'}>
      {userRole === 'seller' && <MobileNavigationSeller />}
      {userRole === 'buyer' && <MobileNavigationBuyer />}
    </div>
  );
};

const AppWrapper = () => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  
  // Show splash screen on route changes
  useEffect(() => {
    console.log('AppWrapper route changed to:', location.pathname);
    setIsLoading(true);
    
    const timer = setTimeout(() => {
      console.log('AppWrapper hiding splash screen');
      setIsLoading(false);
    }, 2000); // 2 seconds for visibility
    
    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Show SplashScreen when loading
  if (isLoading) {
    console.log('AppWrapper showing splash screen');
    return <SplashScreen duration={2000} onFinish={() => {}} />;
  }

  return (
    <CartProvider>
      <WishlistProvider>
        <MessageProvider>
          <ReviewProvider>
            <SettingsProvider>
              <ChatProvider>
                <div className="app">
                  <div>
                    <AppRoutes />
                    
                    {/* Global Components */}
                    <UserChatComponent />
                    <ToastContainer
                      position="top-right"
                      autoClose={5000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      rtl={false}
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="colored"
                      style={{ marginTop: '60px' }} // Add some top margin to avoid overlap with header
                      toastStyle={{ 
                        fontSize: '14px',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }}
                    />
                    <Notification />
                    <MobileNavigationWrapper />
                  </div>
                </div>
                <ChatButton />
              </ChatProvider>
            </SettingsProvider>
          </ReviewProvider>
        </MessageProvider>
      </WishlistProvider>
    </CartProvider>
  );
};

// Main App Component
const App = () => {
  return (
    <React.Fragment>
      <NavigationProvider>
        <AuthProvider>
          <SettingsProvider>
            <MessageProvider>
              <ReviewProvider>
                <ChatProvider>
                  <CartProvider>
                    <WishlistProvider>
                      <MobileMenuProvider>
                        <AppWrapper />
                      </MobileMenuProvider>
                    </WishlistProvider>
                  </CartProvider>
                </ChatProvider>
              </ReviewProvider>
            </MessageProvider>
          </SettingsProvider>
        </AuthProvider>
      </NavigationProvider>
    </React.Fragment>
  );
};

export default App;
